import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { catchError, finalize, of } from 'rxjs';
import { BackendService } from '@ipg/ngx-frontend-toolset';
import { AuthViewComponent } from '../../views/auth/components/auth-view/auth-view.component';

@Component({
  selector: 'app-tenant-lookup-form',
  imports: [
    ReactiveFormsModule,
    AuthViewComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './tenant-lookup-form.component.html',
  styleUrl: './tenant-lookup-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantLookupFormComponent {
  private readonly el = inject(ElementRef);
  private readonly fb = inject(FormBuilder);
  private readonly backend = inject(BackendService);

  // Inputs
  readonly title = input.required<string>();
  readonly message = input.required<string>();
  readonly initialTenantName = input<string>('');
  readonly buttonText = input<string>('Doorgaan');
  readonly showInlineError = input<boolean>(false);

  // Outputs
  readonly lookupSuccess = output<string>();
  readonly lookupError = output<string>();

  // State
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly tenantForm: FormGroup;

  constructor() {
    this.tenantForm = this.fb.group({
      tenantName: [
        '',
        [Validators.required, Validators.minLength(2), Validators.maxLength(50)],
      ],
    });

    afterNextRender(() => {
      this.el.nativeElement.querySelector('input')?.focus();
    });

    // Update form value when initialTenantName changes
    effect(() => {
      const initialName = this.initialTenantName();
      if (initialName) {
        this.tenantForm.patchValue({ tenantName: initialName });
      }
    });
  }

  protected onSubmit(): void {
    if (this.tenantForm.invalid) {
      return;
    }

    const tenantName = this.tenantForm.value.tenantName.trim().toLowerCase();
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.backend
      .setTenantIDByName(tenantName)
      .pipe(
        catchError((error: unknown) => {
          console.error('Tenant lookup failed:', error);

          if (this.showInlineError()) {
            // Show error inline in the form
            this.errorMessage.set(
              'Deze organisatie bestaat nog steeds niet. Controleer de spelling.'
            );
          }

          // Emit error event for parent to handle
          this.lookupError.emit(tenantName);

          return of(null);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe((tenantId: string | null) => {
        if (tenantId) {
          // Success! Emit success event for parent to handle
          this.lookupSuccess.emit(tenantName);
        }
      });
  }
}
